import SPHERELogo from "$root/assets/images/SPHERE_logo.svg";
import CalendarDrawIcon from "$root/assets/images/glyphs[preload]/CalendarDrawIcon.svg";
import CloudClaculatingDrawIcon from "$root/assets/images/glyphs[preload]/CloudClaculatingDrawIcon.svg";
import EmptyBox from "$root/assets/images/glyphs[preload]/EmptyBox.svg";
import InvalidBox from "$root/assets/images/glyphs[preload]/InvalidBox.svg";
import MonitorCalculatingDrawIcon from "$root/assets/images/glyphs[preload]/MonitorCalculatingDrawIcon.svg";
import MonitorErrorDrawIcon from "$root/assets/images/glyphs[preload]/MonitorErrorDrawIcon.svg";
import MonitorWithHourglass from "$root/assets/images/glyphs[preload]/MonitorWithHourglass.svg";
import MonitoringMetricsIcon from "$root/assets/images/glyphs[preload]/MonitoringMetricsIcon.svg";
import SadMachineDrawIcon from "$root/assets/images/glyphs[preload]/SadMachineDrawIcon.svg";
import TextDescriptionDrawIcon from "$root/assets/images/glyphs[preload]/TextDescriptionDrawIcon.svg";
import { useLocaleFormatters } from "$root/localization/hooks";
import { qualifier } from "$root/utils";
import {
	CircularProgressBar,
	Icon,
	type Option,
	ProgressBar,
	type StylableProps,
} from "@mdotm/shadcn/legacy/components";
import { Button, type DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import {
	type NodeOrFn,
	overrideClassName,
	renderNodeOrFn,
	toClassListRecord,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ButtonWithSelect } from "../buttons/ButtonWithSelect";
import "./style.css";
import type { InstrumentEntities } from "$root/functional-areas/instruments-editor/const";
import type { JSX, ReactNode } from "react";

export type WallOverlayProps = {
	children: NodeOrFn;
	overlay: NodeOrFn;
	showOverlay: boolean;
	walledAppearance?: StylableProps;
} & StylableProps;

export function WallOverlay({
	children,
	overlay,
	className,
	style,
	showOverlay,
	walledAppearance,
}: WallOverlayProps): JSX.Element {
	return (
		<div className={toClassName({ "relative z-0": true, ...toClassListRecord(className) })} style={style}>
			{showOverlay && (
				<div
					className="absolute inset-0 z-10"
					style={{
						backgroundColor: "rgba(255, 255, 255, 0.5)",
					}}
				>
					{renderNodeOrFn(overlay)}
				</div>
			)}
			<div
				className={toClassName({ "relative z-0": true, ...toClassListRecord(walledAppearance?.className) })}
				style={walledAppearance?.style}
			>
				{renderNodeOrFn(children)}
			</div>
		</div>
	);
}

export type IconWallBaseProps = {
	defaultBackground?: boolean;
	opaque?: boolean;
	children: NodeOrFn;
} & DataAttributesProps &
	StylableProps;

export function IconWallBase({
	defaultBackground,
	children,
	opaque,
	style,
	className,
	...dataAttrs
}: IconWallBaseProps): JSX.Element {
	return (
		<div
			className={overrideClassName(
				{
					"flex-1 min-h-0 h-full w-full flex items-center justify-center py-4 relative": true,
					DefaultIconWallBackground: (defaultBackground || opaque) ?? true,
					opaque: opaque ?? true,
				},
				className,
			)}
			style={style}
			{...dataAttrs}
		>
			{renderNodeOrFn(children)}
		</div>
	);
}

function decorate<Name extends string, TProps extends Record<string, any>>(
	name: Name,
	Fn: (props: TProps) => JSX.Element,
) {
	return {
		[name]: function WallDecoratorImpl({
			opaque,
			defaultBackground,
			className,
			style,
			...innerProps
		}: TProps & Omit<IconWallBaseProps, "children">): JSX.Element {
			return (
				<IconWallBase
					data-qualifier={`IconWall/${name}`}
					opaque={opaque}
					defaultBackground={defaultBackground}
					className={className}
					style={style}
				>
					<Fn {...(innerProps as TProps)} />
				</IconWallBase>
			);
		},
	} as Record<Name, (props: TProps & Omit<IconWallBaseProps, "children">) => JSX.Element>;
}

export const IconWalls = {
	...decorate("LoadingData", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col">
				<CircularProgressBar
					value="indeterminate"
					className={{ hidden: props.hideDrawIcon, "mx-auto mt-0 mb-2": true }}
				/>
				<h4 className="text-center">{t("WALL_STATES.LOADING_DATA.TITLE")}</h4>
			</div>
		);
	}),
	...decorate("InfiniteProgress", function WallImpl(): JSX.Element {
		const { t } = useTranslation();
		return (
			<>
				<ProgressBar value="indeterminate" className="absolute top-0 inset-x-0" />
				<h4 className="text-center">{t("WALL_STATES.LOADING_DATA.TITLE")}</h4>
			</>
		);
	}),
	...decorate("CalculatingData", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={CloudClaculatingDrawIcon}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center">{t("WALL_STATES.CALCULATING_DATA.TITLE")}</h4>
				<h4 className="text-center">{t("WALL_STATES.CALCULATING_DATA.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate("DataNotAvailable", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={MonitorWithHourglass}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center">{t("WALL_STATES.DATA_NOT_YET_AVAILABLE.TITLE")}</h4>
				<h4 className="text-center">{t("WALL_STATES.DATA_NOT_YET_AVAILABLE.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate("NoBondsToTrack", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={MonitorWithHourglass}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center">{t("WALL_STATES.NO_BONDS_TO_TRACK.TITLE")}</h4>
				<h4 className="text-center">{t("WALL_STATES.NO_BONDS_TO_TRACK.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate("NoCashEvents", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={MonitorWithHourglass}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center">{t("WALL_STATES.NO_CASH_EVENTS.TITLE")}</h4>
				<h4 className="text-center">{t("WALL_STATES.NO_CASH_EVENTS.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate("HistoricalDataNotAvailable", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={MonitorWithHourglass}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center">{t("WALL_STATES.HISTORICAL_DATA_NOT_YET_AVAILABLE.TITLE")}</h4>
				<h4 className="text-center">{t("WALL_STATES.HISTORICAL_DATA_NOT_YET_AVAILABLE.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate("ConstraintNotSet", function WallImpl(props: { constraintName: string }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col items-center">
				<img alt="illustration" src={EmptyBox} />
				<p className="whitespace-pre-line font-semibold">
					<Trans i18nKey="WALL_STATES.CONSTRAINT_NOT_SET.TITLE" values={{ step: props.constraintName }} />
				</p>
				<p>{t("WALL_STATES.CONSTRAINT_NOT_SET.DESCRIPTION")}</p>
			</div>
		);
	}),
	...decorate(
		"ErrorData",
		function WallImpl(props: {
			redirect?: {
				path: string;
				title: string;
			};
			undoButton?: {
				onClick: () => void;
				label: string;
			};
			hideDrawIcon?: boolean;
		}): JSX.Element {
			const { t } = useTranslation();
			return (
				<div className="flex flex-col">
					<img
						alt="illustration"
						src={SadMachineDrawIcon}
						className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
					/>
					<h4 className="text-center">{t("WALL_STATES.NO_DATA.TITLE")}</h4>
					<h4 className="text-center">{t("WALL_STATES.NO_DATA.DESCRIPTION")}</h4>
					{props.redirect && (
						<Link
							to={props.redirect.path}
							className={`mt-4 text-center underline text-[color:var(--global-palette-primary-600)]`}
						>
							<strong>Return to {props.redirect.title}</strong>
						</Link>
					)}
					{props.undoButton && (
						<Button palette="primary" size="small" onClick={props.undoButton.onClick} className="mt-4 self-center">
							{props.undoButton.label}
						</Button>
					)}
				</div>
			);
		},
	),
	...decorate("GenericError", function WallImpl(): JSX.Element {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col space-y-2 items-center h-screen pt-40 bg-gray-100">
				<div>
					<img src={SPHERELogo} alt="Sphere logo" />
				</div>
				<h1 className="font-bold text-4xl">{t("SOMETHING_WENT_WRONG")}</h1>
				<div>
					<Link to="/">
						<h2 className="font-bold underline text-2xl">{t("RETURN_TO_HOME")}</h2>
					</Link>
				</div>
			</div>
		);
	}),
	...decorate(
		"ReviewErrorData",
		function WallImpl(props: {
			section: "portfolio" | "universe";
			onDelete: () => void;
			hideDrawIcon?: boolean;
		}): JSX.Element {
			const { t } = useTranslation();
			return (
				<div className="flex justify-center items-center grow px-3">
					<article className="flex flex-col justify-center items-center gap-4">
						<img
							alt="illustration"
							src={InvalidBox}
							className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
						/>
						<div className="text-center">
							<strong className="text-sm">{t("WALL_STATES.EMPTY_SS_PORTFOLIO.TITLE", { type: props.section })}</strong>
							<p className="whitespace-pre-line">
								{t("WALL_STATES.EMPTY_SS_PORTFOLIO.DESCRIPTION", { type: props.section })}
							</p>
						</div>
						<Button
							onClick={(e) => {
								e.currentTarget.setAttribute("disabled", "true");
								props.onDelete?.();
							}}
							size="small"
							palette="secondary"
						>
							Delete {props.section}
						</Button>
					</article>
				</div>
			);
		},
	),
	...decorate("PortfolioListEmptyData", function WallImpl(_props: { refetch?: () => Promise<void> }): JSX.Element {
		return (
			<div className="flex justify-between items-center grow px-3">
				<div className="flex items-center">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line">
						<Trans
							i18nKey="PORTFOLIO_STUDIO.empty"
							values={{ ptfType: "portfolio" }}
							components={{
								strong: <strong />,
							}}
						/>
					</p>
				</div>
			</div>
		);
	}),
	...decorate("ReferenceListEmptyData", function WallImpl(): JSX.Element {
		return (
			<div className="flex justify-between items-center grow px-3">
				<div className="flex items-center">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line">
						<Trans
							i18nKey="PORTFOLIO_STUDIO.empty"
							values={{ ptfType: "portfolio reference" }}
							components={{
								strong: <strong />,
							}}
						/>
					</p>
				</div>
			</div>
		);
	}),
	...decorate("UniverseListEmptyData", function WallImpl(): JSX.Element {
		return (
			<div className="flex justify-between items-center grow px-3">
				<div className="flex items-center">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line">
						<Trans
							i18nKey="PORTFOLIO_STUDIO.empty"
							values={{ ptfType: "universe" }}
							components={{
								strong: <strong />,
							}}
						/>
					</p>
				</div>
			</div>
		);
	}),
	...decorate("MarketViewListEmptyData", function WallImpl(): JSX.Element {
		return (
			<div className="flex justify-between items-center grow px-3">
				<div className="flex items-center">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line">
						<Trans
							i18nKey="PORTFOLIO_STUDIO.EMPTY_SCENARIO"
							components={{
								strong: <strong />,
							}}
						/>
					</p>
				</div>
			</div>
		);
	}),
	...decorate("PortfolioMonitoringEmptyData", function WallImpl(props: { onCreate: () => void }): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line text-center">
						<strong>You haven’t set anything to be monitored yet.</strong>
						{"\n"}
						Create a proposal to optimise and monitor this portfolio
					</p>
				</div>
				<Button palette="secondary" size="small" className="mt-3" onClick={props.onCreate}>
					Create proposal
				</Button>
			</div>
		);
	}),
	...decorate("PortfolioMonitoringUnconsistency", function WallImpl(): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line text-center">
						<strong>You haven&apos;t set anything to be monitored yet.</strong>
					</p>
				</div>
			</div>
		);
	}),
	...decorate("PortfolioMonitoringHidden", function WallImpl(): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<img alt="illustration" src={EmptyBox} />
					<p className="whitespace-pre-line text-center">
						<strong>Your data monitoring is hidden</strong>
						{"\n"}
						Check your filters in the upper right hand side to show your data monitoring
					</p>
				</div>
			</div>
		);
	}),
	...decorate("Loader", function WallImpl(): JSX.Element {
		return <CircularProgressBar value="indeterminate" />;
	}),
	...decorate("WizardError", function WallImpl(props: { onAsyncClear: () => MaybePromise<void> }): JSX.Element {
		const { t } = useTranslation();
		return (
			<div>
				<img alt="illustration" src={SadMachineDrawIcon} className={toClassName({ "mx-auto my-0": true })} />
				<div className="flex items-center justify-center h-full">
					<div className="grid justify-center">
						<p className="text-lg text-center mb-2">{t("WALL_STATES.NO_DATA.TITLE")}</p>
						<Button
							palette="primary"
							size="small"
							className="mx-auto"
							onClickAsync={props.onAsyncClear}
							data-qualifier="portfolioWizard/iconWall/missingData/clear"
						>
							Clear your portfolio
						</Button>
					</div>
				</div>
			</div>
		);
	}),
	...decorate("EditorEmptyData", function WallImpl(props: { entity: InstrumentEntities }) {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col items-center justify-center h-full">
				<div className="w-fit">
					<img alt="illustration" src={EmptyBox} style={{ width: 103, height: 101 }} />
				</div>

				<div className="grid justify-center text-center not-italic!">
					<p className="font-semibold">There are no assets in your {t(`ENTITY.${props.entity}`)} yet.</p>
					<p className="mb-4">
						Add them either by uploading them from excel, selecting them from your account or add a single row manually
						from the options above
					</p>
				</div>
			</div>
		);
	}),
	...decorate("UniverseWithGroupEmpty", function WallImpl() {
		const { t } = useTranslation();
		return (
			<div className="flex flex-col items-center justify-center h-full">
				<div className="w-fit">
					<img alt="illustration" src={EmptyBox} style={{ width: 103, height: 101 }} />
				</div>

				<div className="grid justify-center text-center not-italic!">
					<p className="font-semibold">There are no assets in your {t("ENTITY.UNIVERSE")} yet.</p>
					<p className="mb-4">Start by creating a group, then add instruments to it.</p>
				</div>
			</div>
		);
	}),
	...decorate("UniverseWithGroupEmptySelection", function WallImpl() {
		return (
			<div className="flex flex-col items-center justify-center h-full">
				<div className="w-fit">
					<img alt="illustration" src={EmptyBox} style={{ width: 103, height: 101 }} />
				</div>

				<div className="grid justify-center text-center not-italic!">
					<p className="font-semibold">You haven't selected any group yet.</p>
				</div>
			</div>
		);
	}),
	...decorate("UniverseWithGroupEmptyFilterResult", function WallImpl() {
		return (
			<div className="flex flex-col items-center justify-center h-full">
				<div className="w-fit">
					<img alt="illustration" src={EmptyBox} style={{ width: 103, height: 101 }} />
				</div>

				<div className="grid justify-center text-center not-italic!">
					<p className="font-semibold">No results.</p>
				</div>
			</div>
		);
	}),
	...decorate("CalculatingApi", function WallImpl() {
		return (
			<>
				<ProgressBar
					value="indeterminate"
					accentColor={themeCSSVars.palette_A300}
					backgroundColor={themeCSSVars.palette_N100}
					className="absolute top-0 inset-x-0"
					barHeight={8}
				/>
				<div className="flex flex-col">
					<h4 className="text-center">Calculating...</h4>
				</div>
			</>
		);
	}),
	...decorate("CalculatingPortfolioWidgetData", function WallImpl(props: { hideDrawIcon?: boolean }) {
		const { t } = useTranslation();

		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={MonitorCalculatingDrawIcon}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center font-semibold">{t("WALL_STATES.CALCULATING_DATA.TITLE")}</h4>
				<h4 className="text-center">{t("WALL_STATES.CALCULATING_DATA.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate(
		"ErrorPortfolioWidgetData",
		function WallImpl(props: {
			redirect?: {
				path: string;
				title: string;
			};
			hideDrawIcon?: boolean;
		}): JSX.Element {
			const { t } = useTranslation();
			return (
				<div className="flex flex-col">
					<img
						alt="illustration"
						src={MonitorErrorDrawIcon}
						className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
					/>
					<h4 className="text-center font-semibold">{t("WALL_STATES.NO_DATA.TITLE")}</h4>
					<h4 className="text-center">{t("WALL_STATES.NO_DATA.DESCRIPTION")}</h4>
					{props.redirect && (
						<Link
							to={props.redirect.path}
							className={`mt-4 text-center underline text-[color:var(--global-palette-primary-600)]`}
						>
							<strong>Return to {props.redirect.title}</strong>
						</Link>
					)}
				</div>
			);
		},
	),
	...decorate(
		"WaitForHistoricalPortfolioWidgetData",
		function WallImpl(props: {
			redirect?: {
				path: string;
				title: string;
			};
			hideDrawIcon?: boolean;
		}): JSX.Element {
			const { t } = useTranslation();
			return (
				<div className="flex flex-col">
					<img
						alt="illustration"
						src={CalendarDrawIcon}
						className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
					/>
					<h4 className="text-center">{t("WALL_STATES.HISTORICAL_DATA_NOT_YET_AVAILABLE.TITLE")}</h4>
					<h4 className="text-center">{t("WALL_STATES.HISTORICAL_DATA_NOT_YET_AVAILABLE.DESCRIPTION")}</h4>
				</div>
			);
		},
	),
	/**
	 * A widget that has no value for the past date the page is pinned to (MDOT-24860). Distinct from
	 * `WaitForHistoricalPortfolioWidgetData` and `CalculatingPortfolioWidgetData`, which both promise the
	 * data is coming: on a date that is already over, it is not — so the wall names the date and points at
	 * the two ways out rather than asking the user to wait for something that will never arrive.
	 */
	...decorate("MetricNotAvailableAsOf", function WallImpl(props: { date: string; hideDrawIcon?: boolean }) {
		const { t } = useTranslation();
		// The same formatter the as-of bar and picker use, so one date does not read three ways on one page.
		const { formatDate } = useLocaleFormatters();
		return (
			<div className="flex flex-col">
				<img
					alt="illustration"
					src={CalendarDrawIcon}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center font-semibold">
					{t("WALL_STATES.METRIC_NOT_AVAILABLE_AS_OF.TITLE", { date: formatDate(props.date) })}
				</h4>
				<h4 className="text-center">{t("WALL_STATES.METRIC_NOT_AVAILABLE_AS_OF.DESCRIPTION")}</h4>
			</div>
		);
	}),
	...decorate("PortfolioMonitoringUnconsistencyV2", function WallImpl(): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<img alt="illustration" src={MonitoringMetricsIcon} />
					<p className="whitespace-pre-line text-center">
						<strong>You haven&apos;t set anything to be monitored yet.</strong>
					</p>
				</div>
			</div>
		);
	}),
	...decorate("PortfolioMonitoringEmptyDataV2", function WallImpl(props: { onCreate: () => void }): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<img alt="illustration" src={MonitoringMetricsIcon} />
					<p className="whitespace-pre-line text-center">
						<strong>You haven&apos;t set anything to be monitored yet.</strong>
						{"\n"}
						Add them to optimise and monitor this portfolio
					</p>
				</div>
				<Button palette="secondary" size="small" className="mt-3" onClick={props.onCreate}>
					<Icon icon="Outline1" />
					Add constraint
				</Button>
			</div>
		);
	}),
	...decorate("PortfolioMonitoringHiddenV2", function WallImpl(): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<img alt="illustration" src={MonitoringMetricsIcon} />
					<p className="whitespace-pre-line text-center">
						<strong>Your data monitoring is hidden</strong>
						{"\n"}
						Check your filters in the upper right hand side to show your data monitoring
					</p>
				</div>
			</div>
		);
	}),
	...decorate(
		"PortfolioCommentaryEmpty",
		function WallImpl(props: {
			onGenerate(value?: string): MaybePromise<void>;
			generateOption: Option<string>[];
			defaultValue?: string;
			hideDrawIcon?: boolean;
		}): JSX.Element {
			const { t } = useTranslation();
			return (
				<div className="flex flex-col items-center">
					<div className="flex items-center flex-col">
						<img
							alt="illustration"
							src={TextDescriptionDrawIcon}
							className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
						/>
						<p className="whitespace-pre-line text-center">
							<strong>No comments yet</strong>
							{"\n"}
							Comment could not be generated automatically
						</p>
					</div>
					<ButtonWithSelect
						options={props.generateOption}
						palette="secondary"
						size="small"
						onClick={props.onGenerate}
						enableSearch
						value={props.defaultValue}
						disabled={props.generateOption.length === 0}
						dataAttributes={{
							buttonDataAttributes: { "data-qualifier": qualifier.widgets.portfolioCommentary.generate },
							selectDataAttributes: { "data-qualifier": qualifier.widgets.portfolioCommentary.selectTemplate },
						}}
						className="mt-3"
					>
						{t("BUTTON.GENERATE")}
					</ButtonWithSelect>
				</div>
			);
		},
	),
	...decorate(
		"WorkspaceMissingConcepts",
		function WallImpl(props: { onClick(): void; buttonContent: ReactNode }): JSX.Element {
			return (
				<div className="flex flex-col items-center">
					<div className="flex items-center flex-col">
						<img alt="illustration" src={MonitoringMetricsIcon} />
						<p className="whitespace-pre-line text-center">
							<strong>You haven&apos;t set anything to be viewed yet.</strong>
							{"\n"}
							Add them to start analyze and monitor this workspace
						</p>
					</div>
					<Button palette="secondary" size="small" className="mt-3" onClick={props.onClick}>
						{props.buttonContent}
					</Button>
				</div>
			);
		},
	),
	...decorate("WorkspaceMissingFilters", function WallImpl(props: { onClick(): void }): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<p className="whitespace-pre-line text-center">
						<strong>You haven&apos;t set any filters yet.</strong>
						{"\n"}
						Add them to start filter this workspace
					</p>
				</div>
				<Button palette="secondary" size="small" className="mt-3" onClick={props.onClick}>
					<Icon icon="Outline1" />
					Add filter
				</Button>
			</div>
		);
	}),
	...decorate("WorkspaceMissingColumns", function WallImpl(props: { onClick(): void }): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<p className="whitespace-pre-line text-center">
						<strong>You haven&apos;t set any columns yet.</strong>
						{"\n"}
						Add them to start display the data of the workspace
					</p>
				</div>
				<Button palette="secondary" size="small" className="mt-3" onClick={props.onClick}>
					<Icon icon="Outline1" />
					Add column
				</Button>
			</div>
		);
	}),
	...decorate("WorkspaceMissingCharts", function WallImpl(props: { onClick(): void }): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<div className="flex items-center flex-col">
					<p className="whitespace-pre-line text-center">
						<strong>You haven&apos;t set any chart yet.</strong>
						{"\n"}
						Add them to start analyze workspace
					</p>
				</div>
				<Button palette="secondary" size="small" className="mt-3" onClick={props.onClick}>
					<Icon icon="Outline1" />
					Add chart
				</Button>
			</div>
		);
	}),
	...decorate("WorkspaceAddChart", function WallImpl(props: { onClick(): void }): JSX.Element {
		return (
			<div className="flex flex-col items-center">
				<Button palette="secondary" size="small" onClick={props.onClick}>
					<Icon icon="Outline1" />
					Add chart
				</Button>
			</div>
		);
	}),
	...decorate("OutdatedClient", function WallImpl(props: { hideDrawIcon?: boolean }): JSX.Element {
		return (
			<div className="flex flex-col items-center max-w-md">
				<img
					alt="illustration"
					src={MonitorErrorDrawIcon}
					className={toClassName({ hidden: props.hideDrawIcon, "mx-auto my-0": true })}
				/>
				<h4 className="text-center font-semibold">This page is unavailable on your version of Sphere</h4>
				<p className="text-center mt-2">
					The data was saved by a newer version of the application. Please refresh the page.
				</p>
				<Button palette="primary" size="small" className="mt-4" onClick={() => window.location.reload()}>
					Refresh
				</Button>
			</div>
		);
	}),
};

export const walls = Object.keys(IconWalls) as (keyof typeof IconWalls)[];
